
import Vue, { PropType } from 'vue';
import _trim from 'lodash/trim';
import { mapActions, mapState } from 'vuex';
import {
	FeedbackReport,
	FeedbackReportComment,
	FeedbackReportStatus,
	FEEDBACK_REPORT_COMMENT_MAX_LENGTH,
} from '@run-diffusion/shared';
import { db, DocumentReference, FieldValue } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import { commentLengthRule, isCommentDeleted, STATUS_LABELS } from './feedbackReports';

// One comment, status line or reply on a feedback report. Its author can change a comment's words,
// or delete it, which stamps deletedAt: the comment stays, as "Comment deleted", with its replies
// under it (firestore.rules holds both to the author, and removes nothing). Status lines stay as
// they were written.
export default Vue.extend({
	name: 'FeedbackCommentItem',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
		comment: { type: Object as PropType<FeedbackReportComment>, required: true },
		reply: { type: Boolean, default: false },
		canReply: { type: Boolean, default: false },
	},
	data () {
		return {
			editing: false,
			draft: '',
			saving: false,
			deleting: false,
			rules: [commentLengthRule],
			isMac: /Mac|iPhone|iPad/.test(navigator.platform || ''),
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		deleted (): boolean {
			return isCommentDeleted(this.comment);
		},
		// its author's to edit or delete, until it is deleted
		isMine (): boolean {
			// an agent's comment carries its developer's uid, but stays as the agent wrote it (firestore.rules)
			return this.comment.kind === 'comment' && !this.deleted && !!this.user && this.comment.author.uid === this.user.id && this.comment.author.kind !== 'agent';
		},
		canSave (): boolean {
			const body: string = _trim(this.draft || '');
			return !!body && body.length <= FEEDBACK_REPORT_COMMENT_MAX_LENGTH && body !== this.comment.body && !this.saving;
		},
		commentTime (): string {
			const at: Date | null = this.timeOf(this.comment.createdAt);
			return at ? at.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'just now';
		},
		editedTitle (): string {
			const at: Date | null = this.timeOf(this.comment.editedAt);
			return at ? `Edited ${at.toLocaleString()}` : 'Edited';
		},
		commentRef (): DocumentReference {
			return db.collection('feedbackReports').doc(this.report.id).collection('comments').doc(this.comment.id);
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		statusLabel (status: FeedbackReportStatus | null): string {
			return status ? STATUS_LABELS[status] : '';
		},
		timeOf (timestamp: { toDate: () => Date } | null | undefined): Date | null {
			return timestamp ? timestamp.toDate() : null;
		},
		startEdit () {
			this.draft = this.comment.body;
			this.editing = true;
			this.$nextTick(() => {
				const editor = this.$refs.editor as Vue | undefined;
				const input: HTMLTextAreaElement | null = editor ? editor.$el.querySelector('textarea') : null;
				if (!input) return;
				input.focus();
				input.setSelectionRange(input.value.length, input.value.length);
			});
		},
		cancelEdit () {
			this.editing = false;
			this.draft = '';
		},
		async save () {
			if (!this.canSave) return;
			this.saving = true;
			try {
				await this.commentRef.update({ body: _trim(this.draft), editedAt: FieldValue.serverTimestamp() });
				this.editing = false;
			} catch (e) {
				console.error('[feedback] failed to edit a comment', this.comment.id, e);
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'Couldn\'t save your edit, try again', show: true });
			} finally {
				this.saving = false;
			}
		},
		async remove (close: () => void) {
			this.deleting = true;
			try {
				await this.commentRef.update({ deletedAt: FieldValue.serverTimestamp() });
				this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message: 'Comment deleted', show: true });
			} catch (e) {
				console.error('[feedback] failed to delete a comment', this.comment.id, e);
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'Couldn\'t delete it, try again', show: true });
			} finally {
				this.deleting = false;
				close();
			}
		},
	},
	components: {
		DoubleConfirmDangerDialog,
	},
});
